import { useEffect } from 'react';
import TimerSurfaceService from '../../services/TimerSurfaceService';
import { recipeTimerRegistry, recipeTimerScope } from './recipeStepTimers';
import { recipeTimerSession } from './recipeTimerIdentity';

export function foregroundRecipeDing() {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const context = new AudioContext();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.frequency.value = 880;
    oscillator.connect(gain);
    gain.connect(context.destination);
    gain.gain.setValueAtTime(0.14, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 0.5);
    oscillator.start();
    oscillator.stop(context.currentTime + 0.5);
    oscillator.onended = () => context.close();
  } catch (_) { /* Autoplay permission is not guaranteed. */ }
}

// Mount at App scope, not at recipe scope. Only one deadline timeout is active;
// a suspended tab recomputes from absolute deadlines when it wakes.
export default function RecipeTimerMonitor({ userId, registry = recipeTimerRegistry, sound = foregroundRecipeDing }) {
  useEffect(() => {
    if (!userId) return undefined;
    const scopeId = recipeTimerScope(userId);
    let timeout;
    let checking = false;
    const check = () => {
      clearTimeout(timeout);
      if (checking) return;
      checking = true;
      try {
        const nativeAlarms = TimerSurfaceService.isNative() && recipeTimerSession.getAlarmStatus(userId)?.ok === true;
        const now = Date.now();
        let next = Infinity;
        for (const [id, item] of Object.entries(registry.all(userId))) {
          if (!item || item.alerted || !Number.isFinite(item.deadlineEpochMs)) continue;
          const firstDeadline = item.deadlineEpochMs - item.targetDurationMs + (item.firstDurationMs ?? item.targetDurationMs);
          if (item.deadlineEpochMs > now) {
            next = Math.min(next, item.deadlineEpochMs - now);
            if (item.firstDurationMs < item.targetDurationMs && !item.firstAlerted) {
              if (firstDeadline > now) next = Math.min(next, firstDeadline - now);
              else if (document.visibilityState === 'visible' && !nativeAlarms) {
                if (now - firstDeadline < 60000) sound();
                registry.markFirstAlerted(userId, id);
              }
            }
          } else if (document.visibilityState === 'visible' && !nativeAlarms) {
            if (now - item.deadlineEpochMs < 60000) sound();
            registry.markAlerted(userId, id);
          }
        }
        if (Number.isFinite(next)) timeout = setTimeout(check, Math.min(next, 2147483647));
      } finally { checking = false; }
    };
    const onChange = event => { if (event?.detail?.scopeId === scopeId) check(); };
    const onStorage = event => { if (event.key === scopeId) check(); };
    const onNative = event => {
      const entry = event?.id && registry.get(userId, event.id);
      if (entry?.completed && !entry.alerted) {
        if (document.visibilityState === 'visible' && recipeTimerSession.getAlarmStatus(userId)?.notificationsAllowed === false &&
            Date.now() - entry.deadlineEpochMs < 60000) sound();
        registry.markAlerted(userId, event.id);
      }
      check();
    };
    const onFirstNative = event => {
      const entry = event?.id && registry.get(userId, event.id);
      if (entry?.firstReached && !entry.completed && !entry.firstAlerted && entry.firstDurationMs < entry.targetDurationMs) {
        if (document.visibilityState === 'visible' && recipeTimerSession.getAlarmStatus(userId)?.notificationsAllowed === false &&
            Date.now() - (entry.deadlineEpochMs - entry.targetDurationMs + entry.firstDurationMs) < 60000) sound();
        registry.markFirstAlerted(userId, event.id);
      }
      check();
    };
    const firstSubscription = TimerSurfaceService.addListener('recipeTimerMilestone', onFirstNative);
    const subscription = TimerSurfaceService.addListener('recipeTimerCompleted', onNative);
    window.addEventListener('recipe-timers-changed', onChange);
    window.addEventListener('storage', onStorage);
    document.addEventListener('visibilitychange', check);
    check();
    return () => {
      clearTimeout(timeout);
      window.removeEventListener('recipe-timers-changed', onChange);
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', check);
      firstSubscription.remove();
      subscription.remove();
    };
  }, [userId, registry, sound]);
  return null;
}
